import { useState } from "react";
import { useIntl } from "react-intl";
import { useMutation, useQuery } from "@apollo/client/react";
import { Link } from "react-router";
import { SystemSettingsDocument, PersonSettingsDocument, UpdatePersonSettingsDocument } from "@/graphql/generated/operations";
import { parseHintText } from "@/lib/hint-links";

const DISMISSED_KEY = "dismissedWelcomeMessage";

export function WelcomeHint() {
  const intl = useIntl();
  const [dismissedLocally, setDismissedLocally] = useState(false);
  const { data: systemSettingsData } = useQuery(SystemSettingsDocument);
  const { data: personSettingsData, loading: personSettingsLoading, refetch: refetchPersonSettings } =
    useQuery(PersonSettingsDocument);
  const [updatePersonSettings] = useMutation(UpdatePersonSettingsDocument);

  const welcomeMessage = systemSettingsData?.systemSettings?.welcomeMessage?.trim() ?? "";
  const personSettings = personSettingsData?.settings.settings ?? {};
  const dismissedWelcomeMessage = personSettings[DISMISSED_KEY];

  const visible =
    welcomeMessage.length > 0 &&
    !personSettingsLoading &&
    dismissedWelcomeMessage !== welcomeMessage &&
    !dismissedLocally;

  if (!visible) return null;

  async function dismiss() {
    try {
      const result = await updatePersonSettings({
        variables: { settings: { ...personSettings, [DISMISSED_KEY]: welcomeMessage } },
      });
      if (result.data?.updateSettings === true) {
        setDismissedLocally(true);
        void refetchPersonSettings();
      }
    } catch {
      // Leave the hint visible — the user can retry the dismiss.
    }
  }

  return (
    <div className="smycles-hint relative mb-6">
      <button
        type="button"
        onClick={() => void dismiss()}
        aria-label={intl.formatMessage({ id: "home.welcomeHint.dismiss", defaultMessage: "Dismiss" })}
        className="absolute right-2 top-2 grid h-5 w-5 place-items-center rounded text-ink/50 transition hover:bg-ink/[0.08] hover:text-ink"
      >
        <svg viewBox="0 0 16 16" aria-hidden="true" className="h-3 w-3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
          <path d="M4 4l8 8M12 4l-8 8" />
        </svg>
      </button>
      <div className="whitespace-pre-wrap pr-6 field-value text-ink">
        {parseHintText(welcomeMessage).map((segment, i) =>
          segment.type === "link" ? (
            <Link key={i} to={segment.href} className="text-accent underline">
              {segment.text}
            </Link>
          ) : (
            <span key={i}>{segment.value}</span>
          ),
        )}
      </div>
    </div>
  );
}
